import { reduceAssessmentClientState } from '@repo/assessment';
import type {
  IAnswerEnglishIqV2Data,
  IEnglishIqResultV2,
  IStartEnglishIqSessionV2Data,
} from '@repo/sdk';
import type {
  IEnglishIqV2PendingAnswer,
  IEnglishIqV2StoredFlow,
} from '../english-iq-v2-stored-flow';
import { toEnglishIqV2ClientItem } from '../helper';
import {
  createEnglishIqV2WebState,
  type IEnglishIqV2WebState,
} from './create-english-iq-v2-web-state';

export type EnglishIqV2WebActionType =
  | { flow: IEnglishIqV2StoredFlow | null; ownerSubject: string; type: 'hydrated' }
  | { data: IStartEnglishIqSessionV2Data; type: 'session_started' }
  | { pendingAnswer: IEnglishIqV2PendingAnswer; type: 'answer_submitted' }
  | { data: IAnswerEnglishIqV2Data; type: 'answer_received' }
  | { idempotencyId: string; type: 'completion_submitted' }
  | { type: 'demo_started' }
  | { message: string; type: 'request_failed' }
  | { result: IEnglishIqResultV2; type: 'completion_received' }
  | { type: 'demo_finished' }
  | { type: 'reset' };

export function reduceEnglishIqV2WebState(
  state: IEnglishIqV2WebState,
  action: EnglishIqV2WebActionType,
): IEnglishIqV2WebState {
  if (action.type === 'hydrated') {
    return reduceHydrated(state, action.flow, action.ownerSubject);
  }
  if (action.type === 'session_started') {
    return reduceSessionStarted(state, action.data);
  }
  if (action.type === 'answer_submitted') {
    return {
      ...state,
      assessment: reduceAssessmentClientState(state.assessment, action),
      error: null,
      pendingAnswer: action.pendingAnswer,
    };
  }
  if (action.type === 'answer_received') {
    return reduceAnswerReceived(state, action.data);
  }
  if (action.type === 'completion_submitted') {
    return {
      ...state,
      error: null,
      pendingCompletionId: state.pendingCompletionId ?? action.idempotencyId,
    };
  }
  return reduceViewAction(state, action);
}

function reduceViewAction(
  state: IEnglishIqV2WebState,
  action: EnglishIqV2WebActionType,
): IEnglishIqV2WebState {
  if (action.type === 'demo_started') {
    return { ...state, view: 'demo' };
  }
  if (action.type === 'request_failed') {
    const assessment = reduceAssessmentClientState(state.assessment, {
      message: action.message,
      type: 'submission_failed',
    });
    return { ...state, assessment, error: action.message };
  }
  if (action.type === 'completion_received') {
    return reduceCompletionReceived(state, action.result);
  }
  if (action.type === 'demo_finished') {
    return reduceDemoFinished(state);
  }
  if (action.type === 'reset') {
    return {
      ...createEnglishIqV2WebState(state.sourceShareId),
      hydrated: true,
      ownerSubject: state.ownerSubject,
    };
  }
  return state;
}

function reduceHydrated(
  state: IEnglishIqV2WebState,
  flow: IEnglishIqV2StoredFlow | null,
  ownerSubject: string,
): IEnglishIqV2WebState {
  return flow
    ? { ...flow, error: null, hydrated: true }
    : {
        ...createEnglishIqV2WebState(state.sourceShareId),
        hydrated: true,
        ownerSubject,
      };
}

function reduceDemoFinished(state: IEnglishIqV2WebState): IEnglishIqV2WebState {
  if (!state.pendingResult) {
    return state;
  }
  const assessment = reduceAssessmentClientState(state.assessment, {
    result: state.pendingResult,
    type: 'result_received',
  });
  return { ...state, assessment, pendingResult: null, view: 'result' };
}

function reduceSessionStarted(
  state: IEnglishIqV2WebState,
  data: IStartEnglishIqSessionV2Data,
): IEnglishIqV2WebState {
  const assessment = reduceAssessmentClientState(state.assessment, {
    attemptId: data.token,
    item: toEnglishIqV2ClientItem(data.item),
    token: data.token,
    type: 'session_started',
  });
  return {
    ...state,
    assessment: {
      ...assessment,
      answeredCount: data.progress.answered,
      totalCount: data.progress.total,
    },
    demo: data.demoPreload,
    error: null,
    expiresAt: data.expiresAt,
    pendingAnswer: null,
    pendingCompletionId: null,
    view: 'assessment',
  };
}

function reduceAnswerReceived(
  state: IEnglishIqV2WebState,
  data: IAnswerEnglishIqV2Data,
): IEnglishIqV2WebState {
  const item = data.item ? toEnglishIqV2ClientItem(data.item) : null;
  const activeAssessment = data.replaced
    ? reduceAssessmentClientState(state.assessment, {
        message: '',
        type: 'submission_failed',
      })
    : state.assessment;
  const assessment =
    data.replaced && item
      ? reduceAssessmentClientState(activeAssessment, {
          item,
          token: data.token,
          type: 'media_replaced',
        })
      : reduceAssessmentClientState(activeAssessment, {
          nextItem: item,
          token: data.token,
          type: 'answer_accepted',
        });
  return {
    ...state,
    assessment: {
      ...assessment,
      answeredCount: data.progress.answered,
      totalCount: data.progress.total,
    },
    demo: data.demoPreload ?? state.demo,
    error: null,
    pendingAnswer: null,
  };
}

function reduceCompletionReceived(
  state: IEnglishIqV2WebState,
  result: IEnglishIqResultV2,
): IEnglishIqV2WebState {
  if (result.kind === 'benchmark') {
    return { ...state, pendingCompletionId: null, pendingResult: result, view: 'demo' };
  }
  const assessment = reduceAssessmentClientState(state.assessment, {
    result,
    type: 'result_received',
  });
  return {
    ...state,
    assessment,
    pendingCompletionId: null,
    pendingResult: null,
    view: 'result',
  };
}
